// Building a zip in the browser, for the owner and vehicle document download.
//
// WHY THE ZIP IS BUILT CLIENT SIDE. The API is a Cloudflare Pages Function on
// workerd. Node stream libraries such as archiver do not run there, so a
// server-built archive was never on the table. The handlers return the pack rows
// and the extract contents and the browser assembles the file.
//
// WHY fflate AND NOT A CDN. web/package.json carries it as a real dependency at
// a pinned version and Vite bundles it. Loading a compressor from a CDN would
// need a script-src the app's CSP does not grant, and would break the moment the
// Cockpit is opened on a bad connection.
//
// WHY NOT THE ZIP WRITER ALREADY IN utils/xlsx.ts. There is one there, and it is
// proven: the month-end .xlsx export has used it since 21 August 2026, because an
// .xlsx IS a zip. Lifting it out to be shared was tried and reverted. The reason
// is the same constraint api/src/runtime/routePolicy.ts documents for itself:
// web/test/xlsx.test.ts loads utils/xlsx.ts under `node --test
// --experimental-strip-types`, Node resolves ESM specifiers literally, and
// web/tsconfig.json cannot spell an import './zip.ts' without
// allowImportingTsExtensions. So the moment xlsx.ts imports a sibling module its
// own test stops loading it. Changing that tsconfig to share ninety lines of
// stable format code, on the module that writes a money document, was the worse
// trade. xlsx.ts is therefore untouched and keeps its private writer; this file
// is the one the download uses, and fflate deflates, which that one does not.
import { zipSync, strToU8, type Zippable } from 'fflate'

export type ZipFile = {
  name: string
  content: string | Uint8Array
}

// Fixed, so two downloads of the same selection produce the same bytes. The
// alternative is a timestamp, which makes every archive different and makes
// "is this the same pack I sent last week" unanswerable.
const FIXED_MTIME = new Date('2026-01-01T00:00:00Z')

/** Builds a zip archive as a Blob. */
export function buildZip(files: ZipFile[]): Blob {
  const tree: Zippable = {}
  for (const file of files) {
    const bytes = typeof file.content === 'string' ? strToU8(file.content) : file.content
    // Level 0 for anything already compressed: a PDF gains nothing from a second
    // pass and costs time on a phone. CSV and text deflate to roughly a fifth.
    const compressed = /\.(pdf|png|jpe?g|zip|docx|xlsx)$/i.test(file.name)
    tree[file.name] = [bytes, { level: compressed ? 0 : 6, mtime: FIXED_MTIME }]
  }
  // fflate types its return as Uint8Array<ArrayBufferLike>, and the DOM lib
  // will not take that as a BlobPart because a SharedArrayBuffer cannot back a
  // Blob. zipSync never returns one. Copying into a plain Uint8Array says so in
  // a way the compiler can check, rather than asserting it with a cast.
  const archive = new Uint8Array(zipSync(tree))
  return new Blob([archive], { type: 'application/zip' })
}

/** Hands a blob to the browser as a download. */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  // Revoked on a timer, not in the same tick as the click: Safari has been
  // observed cancelling the download when the URL goes away immediately.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
